import { translate } from "@/i18n";
import { desktopVoiceMessages } from "@/i18n/messages/desktop-voice";
export type VoiceControlIntent =
  | "cancel_tts"
  | "stop_execution"
  | "pause_listening"
  | "sleep_listening"
  | "use_claude"
  | "use_codex"
  | "use_opencode"
  | "new_chat"
  | "commit_now"
  | "discard_segment";

export type VoiceControlMatch = {
  intent: VoiceControlIntent;
  phrase: string;
  residualText?: string;
};

/** Longer normalized text is never a control phrase. */
export const CONTROL_PHRASE_MAX_LENGTH = 48;

export function normalizeIntentText(text: string): string {
  return text
    .normalize("NFC")
    .toLowerCase()
    .replace(/[‘’]/g, "'")
    .replace(/[^\p{L}\p{N}\s']/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * "Denker" and how dictation mishears it (same aliases as the wake keywords
 * in src-tauri/scripts/denker-wake-model.mjs). Accepted when addressing
 * Denker in a whole-utterance control only; free dictation text is never
 * rewritten.
 */
export const DENKER_NAME_VARIANTS = ["denker", "danker", "tanker", "anchor"];

const EXACT_INTENTS: Array<[VoiceControlIntent, string[]]> = [
  [
    "sleep_listening",
    [
      "go to sleep",
      ...DENKER_NAME_VARIANTS.flatMap((name) => [
        `sleep ${name}`,
        `${name} go to sleep`,
      ]),
    ],
  ],
  [
    "cancel_tts",
    ["stop talking", "stop speaking", "be quiet", "quiet", "pause reading"],
  ],
  [
    "stop_execution",
    ["stop", "stop that", "cancel this", "stop working on that", "wait"],
  ],
  [
    "pause_listening",
    [
      "stop listening",
      "hör auf zuzuhören",
      "exit voice mode",
      "leave voice mode",
      "bye",
      "goodbye",
    ],
  ],
  [
    "new_chat",
    ["new chat", "new chad", "start a new chat", "new conversation"],
  ],
  [
    "use_claude",
    [
      "use claude",
      "use cloud",
      "use claud",
      "use claude code",
      "use cloud code",
    ],
  ],
  [
    "use_codex",
    ["use codex", "use codecs", "use kodecs", "use codec", "use code x"],
  ],
  ["use_opencode", ["use opencode", "use open code", "use open coat"]],
  ["commit_now", ["send", "send it", "that's it", "go", "submit"]],
  [
    "discard_segment",
    ["scratch that", "never mind", "nevermind", "ignore that"],
  ],
];

export function detectVoiceControlIntent(
  text: string,
): VoiceControlMatch | null {
  const newChatDirective = text
    .trim()
    .match(
      /^(?:new chat|new chad|start a new chat|new conversation)\s*[:,;.!?—-]\s*(.+)$/i,
    );
  if (newChatDirective?.[1]?.trim()) {
    return {
      intent: "new_chat",
      phrase: "new chat",
      residualText: newChatDirective[1].trim(),
    };
  }
  const normalized = normalizeIntentText(text);
  if (!normalized || normalized.length > CONTROL_PHRASE_MAX_LENGTH) return null;
  for (const [intent, phrases] of EXACT_INTENTS) {
    if (phrases.includes(normalized)) {
      return { intent, phrase: normalized };
    }
  }
  return null;
}

export function isCommitNowPhrase(text: string): boolean {
  return detectVoiceControlIntent(text)?.intent === "commit_now";
}

export function voiceChatCommandLabel(text: string): string | null {
  const match = detectVoiceControlIntent(text);
  if (!match || match.residualText) return null;
  switch (match.intent) {
    case "sleep_listening":
      return translate(desktopVoiceMessages, "commandSleep");
    case "new_chat":
      return translate(desktopVoiceMessages, "commandNewChat");
    case "use_claude":
      return translate(desktopVoiceMessages, "commandUseCli", {
        cli: "Claude",
      });
    case "use_codex":
      return translate(desktopVoiceMessages, "commandUseCli", { cli: "Codex" });
    case "use_opencode":
      return translate(desktopVoiceMessages, "commandUseCli", {
        cli: "OpenCode",
      });
    default:
      return null;
  }
}

export function voiceCliRuntime(intent: VoiceControlIntent) {
  switch (intent) {
    case "use_claude":
      return "claude_cli" as const;
    case "use_codex":
      return "codex_cli" as const;
    case "use_opencode":
      return "opencode_cli" as const;
    default:
      return null;
  }
}
